import React, { memo, useMemo } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Stack,
  CopyToClipboard,
  Icon,
  IconButton,
  Tooltip
} from 'investira.react.components';
import { dates, formats, validators } from 'investira.sdk';
import { HighchartsReact } from 'investira.react.charts';
import PropTypes from 'prop-types';
import CardState from './CardState';
import CardFooter from './CardFooter';

const wAcoesColors = {
  on: '#f1b40f',
  pn: '#058DC7'
};
const wAcoesLabelMarkerSx = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0
};

/** Renderiza o card com o histórico e os totais de papéis da empresa. */
const QuantidadePapeisCard = memo(props => {
  const {
    pessoaNome,
    historico = [],
    isLoading = false,
    actions: pActions,
    onOpenHistory,
    showChart = true,
    showHistoryButton = true,
    sx,
    ...otherProps
  } = props;
  const xPapeis = useMemo(
    () =>
      [...historico].reverse().find(pItem => {
        return !validators.isNull(pItem?.acao?.on) || !validators.isNull(pItem?.acao?.pn);
      }) || {},
    [historico]
  );
  const xChartData = useMemo(() => {
    const xData = {
      categories: [],
      total: []
    };

    historico.forEach(pItem => {
      const xDate = dates.toDate(pItem.data).getTime();
      const xOn = validators.isNull(pItem?.acao?.on) ? null : pItem.acao.on;
      const xPn = validators.isNull(pItem?.acao?.pn) ? null : pItem.acao.pn;

      // Ignora o período quando não há ações representadas no gráfico.
      if (validators.isNull(xOn) && validators.isNull(xPn)) {
        return;
      }

      xData.categories.push(formats.formatDateCustom(xDate, 'DD/MMM/YYYY'));
      xData.total.push({
        y: (xOn || 0) + (xPn || 0),
        on: xOn,
        pn: xPn
      });
    });

    return xData;
  }, [historico]);
  const xChartOptions = useMemo(() => {
    const xLastCategoryIndex = Math.max(xChartData.categories.length - 1, 0);
    const xEdgePadding = Math.max(xLastCategoryIndex * 0.015, 0.015);

    return {
      title: false,
      chart: {
        type: 'line',
        height: 40,
        margin: [2, 4, 2, 4],
        spacing: [0, 0, 0, 0]
      },
      legend: { enabled: false },
      yAxis: {
        type: 'linear',
        title: { text: 'Total de Papéis' },
        showEmpty: false,
        visible: false
      },
      xAxis: {
        categories: xChartData.categories,
        min: -xEdgePadding,
        max: xLastCategoryIndex + xEdgePadding,
        visible: false
      },
      plotOptions: {
        series: {
          inactiveOtherPoints: true,
          states: {
            inactive: {
              opacity: 0.2
            }
          }
        }
      },
      responsive: {
        condition: {
          maxWidth: 500
        }
      },
      series: [
        {
          name: 'Total de Papéis',
          type: 'line',
          data: xChartData.total,
          pointPlacement: 'on',
          opacity: 1,
          marker: {
            enabled: true,
            radius: 3
          }
        }
      ],
      tooltip: {
        outside: true,
        distance: 12,
        formatter: function () {
          const xDate = this.point.category;
          let xHTML = `<span style="font-size: 1em"><b>${xDate}</b></span><br/>`;
          const xPoint = this.point;

          if (!validators.isNull(xPoint.on)) {
            xHTML += `<span style="color:${
              wAcoesColors.on
            }">●</span>ON: <b>${formats.friendlyNumber(
              xPoint.on,
              2,
              false
            )}</b> (${formats.formatNumber(xPoint.on, 0, true, false)})<br/>`;
          }

          if (!validators.isNull(xPoint.pn)) {
            xHTML += `<span style="color:${
              wAcoesColors.pn
            }">●</span>PN: <b>${formats.friendlyNumber(
              xPoint.pn,
              2,
              false
            )}</b> (${formats.formatNumber(xPoint.pn, 0, true, false)})<br/>`;
          }

          xHTML += `Total de Papéis: <b>${formats.friendlyNumber(
            xPoint.y,
            2,
            false
          )}</b> (${formats.formatNumber(xPoint.y, 0, true, false)})<br/>`;

          return xHTML;
        }
      }
    };
  }, [xChartData]);

  const pvFormatNumber = (pValue, pDecimals = 0) => {
    if (validators.isNull(pValue)) {
      return '-';
    }

    if (pActions?.formatNumber) {
      return pActions.formatNumber(pValue, false, pDecimals);
    }

    return formats.formatNumber(pValue, pDecimals, false, false);
  };
  const pvSomaPapeis = (pOn, pPn) => (pOn || 0) + (pPn || 0);
  const xTotalPapeis = pvSomaPapeis(xPapeis?.acao?.on, xPapeis?.acao?.pn);
  const xShowCard = !validators.isEmpty(xPapeis);

  if (!isLoading && !xShowCard) {
    return null;
  }

  return (
    <Stack sx={{ flex: 1, minWidth: 0, ...sx }} {...otherProps}>
      <Card
        sx={{ minHeight: '100%' }}
        aria-label={pessoaNome ? `Quantidade de papéis: ${pessoaNome}` : 'Quantidade de papéis'}>
        <CardContent sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" spacing={2}>
            <Stack flexGrow={1}>
              <Typography variant="caption" color="textPrimary" gutterBottom component="h4">
                Quantidade de papéis
              </Typography>
            </Stack>
            {xShowCard && showHistoryButton ? (
              <Stack>
                <Tooltip title="Histórico Quantidade de Papéis">
                  <div>
                    <IconButton color="primary" onClick={() => onOpenHistory?.('papeis')}>
                      <Icon iconName="chart_bar" size={16} />
                    </IconButton>
                  </div>
                </Tooltip>
              </Stack>
            ) : null}
          </Stack>

          <CardState loading={isLoading} data={xPapeis}>
            <Stack spacing={1} justifyContent="space-between" sx={{ minHeight: '100%' }}>
              <Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h4" color="textPrimary" sx={{ fontWeight: 600 }}>
                    {pvFormatNumber(xTotalPapeis, 2)}
                  </Typography>
                  <CopyToClipboard value={xTotalPapeis} />
                </Stack>

                {!validators.isNull(xPapeis?.data) ? (
                  <Typography variant="caption" color="textPrimary" gutterBottom>
                    Data de referência {formats.formatDateCustom(xPapeis.data, 'DD/MMM/YYYY')}
                  </Typography>
                ) : null}
              </Stack>

              {showChart && !validators.isEmpty(xChartData.categories) ? (
                <Stack minWidth={0} sx={{ width: '100%' }}>
                  <HighchartsReact options={xChartOptions} />
                </Stack>
              ) : null}

              <Stack direction="row" justifyContent="flex-start" spacing={4} sx={{ mt: 2 }}>
                {!validators.isNull(xPapeis?.acao?.on) ? (
                  <Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Stack sx={{ ...wAcoesLabelMarkerSx, backgroundColor: wAcoesColors.on }} />
                      <Typography variant="caption" color="textSecondary">
                        ON
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 600 }}>
                        {pvFormatNumber(xPapeis.acao.on, 2)}
                      </Typography>
                      <Stack>
                        <CopyToClipboard value={xPapeis.acao.on} />
                      </Stack>
                    </Stack>
                  </Stack>
                ) : null}

                {!validators.isNull(xPapeis?.acao?.pn) ? (
                  <Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Stack sx={{ ...wAcoesLabelMarkerSx, backgroundColor: wAcoesColors.pn }} />
                      <Typography variant="caption" color="textSecondary">
                        PN
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 600 }}>
                        {pvFormatNumber(xPapeis.acao.pn, 2)}
                      </Typography>
                      <Stack>
                        <CopyToClipboard value={xPapeis.acao.pn} />
                      </Stack>
                    </Stack>
                  </Stack>
                ) : null}
              </Stack>

              <CardFooter
                consolidacao={xPapeis?.consolidacao_tipo}
                atualizado={xPapeis?.data}
                origem={xPapeis?.origem}
                pc={xPapeis?.pc}
              />
            </Stack>
          </CardState>
        </CardContent>
      </Card>
    </Stack>
  );
});

QuantidadePapeisCard.displayName = 'QuantidadePapeisCard';

QuantidadePapeisCard.propTypes = {
  pessoaNome: PropTypes.string,
  historico: PropTypes.array,
  isLoading: PropTypes.bool,
  actions: PropTypes.shape({
    formatNumber: PropTypes.func
  }),
  onOpenHistory: PropTypes.func,
  showChart: PropTypes.bool,
  showHistoryButton: PropTypes.bool,
  sx: PropTypes.object
};

export default QuantidadePapeisCard;
